import { Button, Card, Stack, ToggleButton, ToggleButtonGroup, Tooltip, useElementBreakpoints } from "@compass/core";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import { BulkSelectedItemErrors } from "generated/openApi";
import pluralize from "pluralize";
import { ReactNode, useCallback, useEffect, useState } from "react";
import ApiService from "services/apiService";
import { BulkSelectedItems } from "./BulkSelectedItems";

export type BulkSelectedViewMode = "Detailed" | "Standard";

export const BulkSelected = <T extends { id: number }>(props: {
  label: string;
  items: Map<any, T>;
  setItems: (value: Map<any, T>) => void;
  getItemLabel: (item: T) => ReactNode;
  errors: BulkSelectedItemErrors[];
  setErrors: (value: BulkSelectedItemErrors[]) => void;
  quickAdd?: React.ReactNode;
}) => {
  const { breakpointsRef, setBreakpoints } = useElementBreakpoints();
  const [gridView, setGridView] = useState<boolean>(false);
  const [errorFilter, setErrorFilter] = useState<boolean>(false);
  const hasErrors = props.errors?.length > 0;
  const [openQuickAdd, setOpenQuickAdd] = useState(false);

  useEffect(() => {
    ApiService.common.getBulkSelectResource().then((response) => {
      setGridView(response.gridView);
    });
  }, []);

  const removeSelectionWithErrors = () => {
    const removingIds = props.errors.map((x) => x.id);
    const newItems = new Map(props.items);
    removingIds.forEach((id) => newItems.delete(id));
    props.setItems(newItems);
    props.setErrors([]);
  };

  // useCallback to help memoize BulkSelectedItem
  const remove = useCallback(
    (id: number) => {
      const newItems = new Map(props.items);
      newItems.delete(id);
      props.setItems(newItems);
    },
    [props.items]
  );

  const getItemLabel = useCallback(props.getItemLabel, []);

  const onChangeGridView = (e: React.MouseEvent, value: boolean) => {
    if (value === undefined) return;
    setGridView(value);
    ApiService.userSetting.saveBulkGridView(value);
  };

  const selectedCount = Array.from(props.items).length;

  const header = (
    <Stack direction="row" justifyContent={"space-between"} alignItems={"center"} spacing="sm">
      <Stack direction="row" spacing="md" alignItems="center">
        <Stack>Selected: {selectedCount}</Stack>

        <ToggleButtonGroup value={gridView} onChange={onChangeGridView} size="small" exclusive>
          <ToggleButton value={false}>
            <ViewHeadlineIcon color="info"/>
          </ToggleButton>
          <ToggleButton value={true}>
            <ViewCompactIcon color="info"/>
          </ToggleButton>
        </ToggleButtonGroup>

        {hasErrors && (
          <Tooltip title="This will show only the selected entries that have errors.">
            <ToggleButtonGroup
              value={errorFilter}
              onChange={(e, value) => setErrorFilter(value ?? false)}
              color="error"
            >
              <ToggleButton value={true} size="small" sx={{ textTransform: "none" }}>
                Errors Filter
              </ToggleButton>
            </ToggleButtonGroup>
          </Tooltip>
        )}
      </Stack>

      <Stack direction="row" spacing="sm" alignItems="center">
        {hasErrors && (
          <Tooltip title="This removes the selected entries that have errors">
            <Button onClick={removeSelectionWithErrors} variant="outlined" color="error">
              Remove
            </Button>
          </Tooltip>
        )}
        {selectedCount > 0 && (
          <Button onClick={() => props.setItems(new Map())} variant="outlined" color="info">
            Clear
          </Button>
        )}
      </Stack>
    </Stack>
  );

  return (
    <Card header={header}>
      <Stack ref={breakpointsRef} spacing="lg">
        {props.quickAdd && (
          <>
            {!openQuickAdd ? (
              <Stack direction="row">
                <Button onClick={() => setOpenQuickAdd(true)} variant="outlined">
                  + Assign {pluralize(props.label)}
                </Button>
              </Stack>
            ) : (
              props.quickAdd
            )}
          </>
        )}

        <BulkSelectedItems
          gridView={gridView}
          items={props.items}
          // @ts-ignore
          getItemLabel={getItemLabel}
          onRemove={remove}
          errors={props.errors}
          errorFilter={errorFilter}
        />
      </Stack>
    </Card>
  );
};
